import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styles from "./BGLevelSummary.module.css";
import Expand from "../../../../../assets/images/expand.svg";
import Chevron from "../../../../../assets/images/Chvron.svg";

import {
  groupByBG,
  initOpenState,
  SUMMARY_NUM_COLS as numCols,
  SUMMARY_PERCENT_COLS as percentCols,
  formatPercent,
} from "./bgSummaryUtils.js";

const splitChildrenByTotal = (children = []) => {
  const totalChild = children.find(
    (r) => r?.is_bg_total === 1 || (r?.["Small c"] || "") === "TOTAL"
  );

  return {
    totalChild,
    restChildren: totalChild
      ? children.filter((r) => r !== totalChild)
      : children,
  };
};

export default function BGLevelSummary({ payload }) {
  const [expandedView, setExpandedView] = useState(false);

  const payloadData = payload?.data;
  const { groups, orderedBGs } = useMemo(() => groupByBG(payloadData ?? []), [payloadData]);
  const [open, setOpen] = useState(() => initOpenState(orderedBGs));

  // ✅ FORMATTERS
  const formatToCappedUnit = (value) => {
    if (value === null || value === undefined) return "-";
    if (value < 1000) return `${value}`;
    if (value < 100000) return `${(value / 1000).toFixed(1)} K`;
    if (value < 10000000) return `${(value / 100000).toFixed(1)} L`;
    return `${(value / 10000000).toFixed(1)} Cr`;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";

    if (typeof value === "number") {
      if (value >= 10000) return formatToCappedUnit(value);
      return value.toLocaleString("en-IN");
    }

    return value;
  };

  React.useEffect(() => {
    setOpen((prev) => {
      const next = { ...prev };
      let changed = false;

      orderedBGs.forEach((bg) => {
        if (!(bg in next)) {
          next[bg] = false;
          changed = true;
        }
      });

      Object.keys(next).forEach((bg) => {
        if (!orderedBGs.includes(bg)) {
          delete next[bg];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [orderedBGs]);

  const toggle = (bg) =>
    setOpen((s) => ({ ...s, [bg]: !s[bg] }));

  const renderNumberCells = (row, rowKey) =>
    numCols.map((c) => (
      <td key={`${rowKey}-${c}`} title={row?.[c]}>
        {formatValue(row?.[c])}
      </td>
    ));

  const renderPercentCells = (row, rowKey) =>
    percentCols.map((c) => (
      <td key={`${rowKey}-${c}`} className={styles.kpiPct}>
        {formatPercent(row?.[c])}
      </td>
    ));

  const renderChildRow = (bg, row, idx, rowKeyPrefix = "child") => (
    <tr key={`${bg}-${rowKeyPrefix}-${idx}`} className={styles.childRow}>
      <td className={`${styles.stickyLeft} ${styles.childBW}`}>
        {row?.["Small c"] ?? "-"}
      </td>

      {renderNumberCells(row, `${bg}-${rowKeyPrefix}-${idx}`)}
      {renderPercentCells(row, `${bg}-${rowKeyPrefix}-${idx}`)}
    </tr>
  );

  const renderGroupRows = (bg) => {
    const grp = groups.get(bg);
    const total = grp?.total || {};
    const { totalChild, restChildren } = splitChildrenByTotal(grp?.children || []);

    return (
      <React.Fragment key={bg}>
        <tr
          className={`${styles.parentRow} ${bg === "TOTAL" ? styles.totalRow : ""
            }`}
        >
          <td className={`${styles.stickyLeft} ${styles.bwCell}`}>
            <button className={styles.chevBtn} onClick={() => toggle(bg)}>
              {bg !== "TOTAL" && (
                <span className={`${styles.chev} ${open[bg] ? styles.chevOpen : ""}`}>
                  <img src={Chevron} alt="Chevron" />
                </span>
              )}
            </button>
            <span className={styles.bwText}>{bg}</span>
          </td>

          {renderNumberCells(total, bg)}
          {renderPercentCells(total, bg)}
        </tr>

        {open[bg] && (
          <>
            {totalChild && renderChildRow(bg, totalChild, "total", "total")}
            {restChildren.map((row, idx) => renderChildRow(bg, row, idx))}
          </>
        )}
      </React.Fragment>
    );
  };

  const noDataAvailable = !payloadData || payloadData.length === 0;

  const tableMarkup = (
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colBW} />
        {numCols.map((c) => (
          <col key={`n-${c}`} />
        ))}
        {percentCols.map((c) => (
          <col key={`p-${c}`} />
        ))}
      </colgroup>

      <thead className={styles.thead}>
        <tr>
          <th className={styles.stickyLeft}>BG</th>
          {numCols.map((c) => (
            <th key={c}>{c}</th>
          ))}
          {percentCols.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>

      <tbody>
        {orderedBGs.map(renderGroupRows)}
      </tbody>
    </table>
  );

  return (
    <div className={styles.wrap}>
      {/* HEADER */}
      <div className={styles.expandWrap}>
        <span className={styles.title}>
          Summary at BG Level
        </span>

        <div className={styles.segmentedBtn}>
          <button
            type="button"
            className={styles.segment}
            onClick={() => setExpandedView(true)}
            disabled={noDataAvailable}
          >
            <img src={Expand} alt="Expand" className={styles.icon} />
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className={styles.tableOuter}>
        <div className={styles.scrollArea}>
          {noDataAvailable ? (
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              No data available
            </div>
          ) : (
            tableMarkup
          )}
        </div>
      </div>

      {/* EXPANDED VIEW */}
      {expandedView && !noDataAvailable && (
        <button
          className={styles.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setExpandedView(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setExpandedView(false);
          }}
          
          tabIndex={0}
          aria-label="Close expanded view"
        >
          <div className={styles.zoomCard}>
            <div className={styles.zoomHeader}>
              <span className={styles.zoomTitle}>
                Summary at BG Level (Expanded)
              </span>

              <button
                className={styles.closeBtn}
                onClick={() => setExpandedView(false)}
              >
                x
              </button>
            </div>

            <div className={styles.zoomScrollArea}>
              {tableMarkup}
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

BGLevelSummary.propTypes = {
  payload: PropTypes.shape({
    data: PropTypes.array,
  }),
};